import { ApolloClient, ApolloLink, InMemoryCache, HttpLink, from, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { auth } from './firebase';

// Use emulator URL in development, relative URL in production
// In emulator: http://localhost:5001/planapus-d83dd/us-central1/api/graphql
const isDevelopment = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '0.0.0.0';

const graphqlUrl = isDevelopment
  ? 'http://localhost:5001/planapus-d83dd/us-central1/api/graphql'
  : (process.env.REACT_APP_GRAPHQL_ENDPOINT || '/api/graphql');

const httpLink = new HttpLink({
  uri: graphqlUrl,
  credentials: 'include',
});

// S10 / ADR-0004 — anonymous principal session token storage. The
// magic-link landing page calls `startAnonymousRunSession`, stores
// the resulting JWT here, and we attach it on every GraphQL request
// via the `X-Anonymous-Token` header. The backend prefers the
// anonymous token when present (no Firebase Bearer needed) and falls
// back to the Firebase Bearer for signed-in users.
//
// Persisted in sessionStorage rather than localStorage on purpose:
// closing the tab ends the anonymous run session, matching the
// magic-link product intent.
const ANON_TOKEN_KEY = 'planapus.anonRunToken';

export function setAnonymousRunToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (!token) {
    window.sessionStorage.removeItem(ANON_TOKEN_KEY);
    return;
  }
  window.sessionStorage.setItem(ANON_TOKEN_KEY, token);
}

export function getAnonymousRunToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(ANON_TOKEN_KEY);
}

// Authentication link that adds Firebase ID token to requests
const authLink = setContext(async (operation, { headers }) => {
  // Get the current user's ID token
  const user = auth.currentUser;
  let token = null;

  if (user) {
    try {
      token = await user.getIdToken();
      // Log token info for debugging (just first/last chars). Dev-only —
      // every GraphQL request would otherwise print a line in the
      // production console (PLN-401).
      if (isDevelopment) {
        const tokenPreview = token ? `${token.slice(0, 10)}...${token.slice(-10)}` : 'null';
        console.log(`authLink: Got token for ${operation.operationName}: ${tokenPreview}`);
      }
    } catch (error) {
      console.error('Error getting ID token:', error);
    }
  } else if (isDevelopment) {
    console.log('authLink: No current user');
  }

  const anonymousToken = getAnonymousRunToken();

  // Return headers with authorization token. We send BOTH the
  // Firebase Bearer (when present) and the anonymous JWT (when
  // present). The backend's `getPrincipalFromContext` (S10) prefers
  // the anonymous header when both are present — that's the right
  // precedence for the magic-link consumer who is mid-claim. The
  // dedicated `claimAnonymousRun` mutation reads the anonymous
  // token from its input arg AND requires the Firebase Bearer to
  // identify the new owner; both flow through this link unchanged.
  const nextHeaders: Record<string, string> = {
    ...headers,
    authorization: token ? `Bearer ${token}` : '',
  };
  if (anonymousToken) {
    nextHeaders['X-Anonymous-Token'] = anonymousToken;
  }
  return { headers: nextHeaders };
});

// Track if a token refresh is already in progress to prevent concurrent refreshes
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Get a fresh token, deduplicating concurrent refresh requests
async function getRefreshedToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const newToken = await user.getIdToken(true);
        if (isDevelopment) console.log('Token refreshed successfully');
        return newToken;
      }
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ============================================================================
// Auth error detection — single source of truth
// ============================================================================

function isGraphQLAuthError(err: { extensions?: Record<string, any>; message?: string }): boolean {
  return !!(
    err.extensions?.code === 'UNAUTHENTICATED' ||
    err.message?.toLowerCase().includes('unauthenticated') ||
    err.message?.toLowerCase().includes('unauthorized') ||
    err.message?.toLowerCase().includes('invalid or expired token')
  );
}

// ============================================================================
// Error link — refresh token + retry, redirect on failure
// ============================================================================

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const hasAuthError = graphQLErrors?.some(isGraphQLAuthError);

  const hasNetworkAuthError =
    networkError && 'statusCode' in networkError && networkError.statusCode === 401;

  if (hasAuthError || hasNetworkAuthError) {
    console.warn('Authentication error detected, attempting token refresh...');

    return new Observable((observer) => {
      let unsubscribe: (() => void) | undefined;

      getRefreshedToken()
        .then((newToken) => {
          if (newToken) {
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `Bearer ${newToken}`,
              },
            });

            const subscriber = forward(operation).subscribe({
              next: (result) => {
                // If the retried result still has auth errors, the refresh didn't
                // help (e.g. token revoked). Redirect instead of looping.
                if (result.errors?.some(isGraphQLAuthError)) {
                  console.warn('Retry still has auth errors, redirecting to auth...');
                  redirectToAuth();
                  observer.complete();
                } else {
                  observer.next(result);
                }
              },
              error: (err) => {
                console.error('Retry failed:', err);
                redirectToAuth();
                // Complete instead of propagating — redirect handles UX
                observer.complete();
              },
              complete: () => observer.complete(),
            });

            unsubscribe = () => subscriber.unsubscribe();
          } else {
            console.warn('No user session, redirecting to auth...');
            redirectToAuth();
            observer.complete();
          }
        })
        .catch((refreshError) => {
          console.error('Token refresh failed:', refreshError);
          redirectToAuth();
          observer.complete();
        });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    });
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`);
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// ============================================================================
// Safety-net link — strip any auth errors that slip through (e.g. via
// errorPolicy:'all' or race conditions with polling queries). This runs
// closest to the cache so components never see UNAUTHENTICATED errors.
// ============================================================================

const stripAuthErrorsLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((result) => {
    if (result.errors?.some(isGraphQLAuthError)) {
      // Auth errors that reach here weren't recoverable — redirect to login
      console.warn('Unrecoverable auth error reached safety-net link, redirecting...');
      redirectToAuth();
      return {
        ...result,
        errors: result.errors.filter((e) => !isGraphQLAuthError(e)) || undefined,
      };
    }
    return result;
  });
});

// Debounce redirects — prevent multiple concurrent auth errors from firing
// multiple redirects (e.g. several polling queries all failing at once)
let redirectPending = false;

// Helper to redirect to auth page
function redirectToAuth() {
  if (redirectPending) return;
  redirectPending = true;

  // Use clearStore safely — apolloClient may not be initialized yet
  try { apolloClient?.clearStore(); } catch { /* noop */ }
  auth.signOut().catch((e) => console.error('Error signing out:', e));

  const currentPath = window.location.hash.slice(1) || '/';
  if (currentPath !== '/auth') {
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    window.location.hash = '#/auth';
  }

  // Reset after a short delay so future sessions can redirect again
  setTimeout(() => { redirectPending = false; }, 2000);
}

export const apolloClient = new ApolloClient({
  link: from([stripAuthErrorsLink, errorLink, authLink, httpLink]),
  // PLN-401: gate the Apollo DevTools connection (and its "Download the
  // Apollo DevTools…" console nag) to development only. Apollo Client
  // 3.14+ moved `connectToDevTools` under the `devtools` option; using
  // the old top-level key emits a deprecation warning on every page
  // load. (Audit #14 from 2026-05-15.)
  devtools: {enabled: isDevelopment},
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          sourceItemsConnection: {
            keyArgs: ['orgId', 'filter'],
            merge(existing: any, incoming: any, { args }: any) {
              if (!args?.after || !existing) {
                return incoming;
              }
              return {
                ...incoming,
                edges: [...existing.edges, ...incoming.edges],
              };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});
